<template>
  <div v-if="deviceInfo" class="flex flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
    <!-- Header Summary Card -->
    <div class="p-6 rounded-2xl bg-primary/5 border border-primary-500/10 flex items-center justify-between overflow-hidden relative">
      <div class="absolute -right-4 -bottom-4 text-8xl opacity-10 rotate-12 select-none pointer-events-none">
        {{ isMobile ? '📱' : '💻' }}
      </div>
      <div class="flex flex-col gap-1 relative z-10">
        <h3 class="text-[10px] font-black uppercase tracking-widest text-primary/70">Detected Mode</h3>
        <p class="text-3xl font-black text-primary">{{ isMobile ? 'Mobile' : 'Desktop' }}</p>
      </div>
      <div class="text-5xl relative z-10">{{ isMobile ? '📱' : '💻' }}</div>
    </div>

    <!-- Grid Layout -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      
      <!-- Device Discovery Card -->
      <div class="p-6 rounded-2xl bg-gray-50/50 dark:bg-white/5 border border-gray-100 dark:border-white/5 flex flex-col items-center text-center gap-4">
        <div class="w-16 h-16 rounded-full bg-white dark:bg-white/10 flex items-center justify-center text-3xl shadow-sm">
          {{ deviceInfo.isLargerScreen ? '🖥️' : '📱' }}
        </div>
        <div>
          <h4 class="font-bold text-gray-900 dark:text-white leading-tight">Form Factor</h4>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase tracking-wider">{{ deviceInfo.isLargerScreen ? 'Tablet or Desktop' : 'Phone' }}</p>
        </div>
        <div class="w-full pt-4 mt-auto border-t border-gray-100 dark:border-white/10 flex flex-col gap-1">
           <p class="text-[10px] uppercase font-bold text-gray-400 tracking-widest">Est. Diagonal</p>
           <p class="text-4xl font-black text-primary tracking-tighter">{{ deviceInfo.diagonalInches }}"</p>
        </div>
      </div>

      <!-- Viewport & DPI Card -->
      <div class="p-6 rounded-2xl bg-gray-50/50 dark:bg-white/5 border border-gray-100 dark:border-white/5 flex flex-col gap-4">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <Maximize2 class="w-5 h-5" />
          </div>
          <h4 class="font-bold text-sm uppercase tracking-wider">Viewport & DPI</h4>
        </div>
        <div class="space-y-3">
          <div v-for="spec in viewportSpecs" :key="spec.label" class="flex justify-between items-center group">
            <span class="text-xs text-gray-500 dark:text-gray-400 transition-colors group-hover:text-primary">{{ spec.label }}</span>
            <span class="text-xs font-mono font-bold bg-gray-100 dark:bg-white/10 px-2 py-1 rounded">{{ spec.value }}</span>
          </div>
        </div>
      </div>

      <!-- Technical Meta Card -->
      <div class="p-6 rounded-2xl bg-gray-50/50 dark:bg-white/5 border border-gray-100 dark:border-white/5 flex flex-col gap-4">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <Monitor class="w-5 h-5" />
          </div>
          <h4 class="font-bold text-sm uppercase tracking-wider">Screen Metrics</h4>
        </div>
        <div class="space-y-3">
          <div v-for="spec in screenSpecs" :key="spec.label" class="flex justify-between items-center group">
            <span class="text-xs text-gray-500 dark:text-gray-400 transition-colors group-hover:text-primary">{{ spec.label }}</span>
            <span class="text-xs font-mono font-bold bg-gray-100 dark:bg-white/10 px-2 py-1 rounded">{{ spec.value }}</span>
          </div>
        </div>
      </div>

      <!-- Physical Estimation Card -->
      <div class="p-6 rounded-2xl bg-gray-50/50 dark:bg-white/5 border border-gray-100 dark:border-white/5 flex flex-col gap-4">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <Ruler class="w-5 h-5" />
          </div>
          <h4 class="font-bold text-sm uppercase tracking-wider">Size Estimation</h4>
        </div>
        <div class="space-y-3">
          <div v-for="spec in estSpecs" :key="spec.label" class="flex justify-between items-center group">
            <span class="text-xs text-gray-500 dark:text-gray-400 transition-colors group-hover:text-primary">{{ spec.label }}</span>
            <span class="text-xs font-mono font-bold text-primary">{{ spec.value }}</span>
          </div>
        </div>
      </div>

    </div>

    <div class="mt-4 p-4 rounded-xl border border-dashed border-gray-200 dark:border-white/10 bg-gray-50/30 dark:bg-transparent">
      <p class="text-[10px] text-center text-gray-400 dark:text-gray-500 leading-relaxed uppercase tracking-widest font-medium">
        ⚠️ Estimation assumes 96 CSS base PPI. Actual physical dimensions vary by device screen density.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, onMounted, ref } from 'vue'
import { KEY_IS_MOBILE } from "@/keys/keys.js";
import { Monitor, Maximize2, Ruler } from 'lucide-vue-next'

const deviceInfo = ref<any>(null)
const isMobile = inject(KEY_IS_MOBILE)

onMounted(() => {
  const dpr = window.devicePixelRatio || 1
  const screenWidthPx = window.innerWidth
  const screenHeightPx = window.innerHeight

  const basePPI = 96
  const physicalPPI = basePPI * dpr

  const widthInches = (screenWidthPx * dpr) / physicalPPI
  const heightInches = (screenHeightPx * dpr) / physicalPPI
  const diagonalInches = Math.sqrt(widthInches ** 2 + heightInches ** 2)

  deviceInfo.value = {
    dpr,
    screenWidthPx,
    screenHeightPx,
    widthInches: widthInches.toFixed(2),
    heightInches: heightInches.toFixed(2),
    diagonalInches: diagonalInches.toFixed(2),
    isLargerScreen: diagonalInches > 7,
    screenResolution: `${screen.width} x ${screen.height}`,
    viewport: `${window.innerWidth} x ${window.innerHeight}`,
    devicePixelRatio: window.devicePixelRatio,
    colorDepth: screen.colorDepth,
    orientation: screen.orientation?.type || 'unknown'
  }
})

const viewportSpecs = computed(() => {
  if (!deviceInfo.value) return []
  const d = deviceInfo.value
  return [
    { label: 'Viewport', value: d.viewport },
    { label: 'DPR', value: d.devicePixelRatio },
    { label: 'DPR (calc)', value: d.dpr },
  ]
})

const screenSpecs = computed(() => {
  if (!deviceInfo.value) return []
  const d = deviceInfo.value
  return [
    { label: 'Resolution', value: d.screenResolution },
    { label: 'Color Depth', value: `${d.colorDepth} bit` },
    { label: 'Orientation', value: d.orientation },
  ]
})

const estSpecs = computed(() => {
  if (!deviceInfo.value) return []
  const d = deviceInfo.value
  return [
    { label: 'Width (Est)', value: `${d.widthInches}"` },
    { label: 'Height (Est)', value: `${d.heightInches}"` },
    { label: 'Diagonal (Est)', value: `${d.diagonalInches}"` },
  ]
})
</script>